<script lang="ts">
    import { onMount, tick } from 'svelte';
    import { getBlogPost, type BlogPost } from '../../api/blogs.ts';
    import Button from '../../lib/Button.svelte';
    import CaretRightIcon from '../../icons/CaretRightIcon.svelte';
    import * as t from "../../translations/News.ts";
    import SkeletonLoaderBlog from '../../lib/News/SkeletonLoaderBlog.svelte';
    import ShareSection from '../../lib/ShareSection.svelte';
    import { push } from 'svelte-spa-router';
    import { vvSettings } from '../../api/common.ts';

    export let params: { id: string };

    let blog: BlogPost | null = null;
    let loading = true;
    let error: string | null = null;
    let currentUrl = '';
    
    const showBlogs = vvSettings?.showBlogs ?? true;
    const pageHeader = showBlogs ? t.Page_Header() : t.News_Tab();

    async function loadBlog() {
        loading = true;
        error = null;
        try {
            blog = await getBlogPost(parseInt(params.id));
        } catch (e) {
            error = 'Failed to load blog post.';
        } finally {
            loading = false;
            currentUrl = window.location.href;
        }
    }

    onMount(loadBlog);
    $: params.id, loadBlog();

    $: if (!loading && blog) {
        tick().then(() => {
            document.dispatchEvent(new CustomEvent("vvContentLoaded"));
        });
    }

    const goBack = () => {
        push('/');
    };
</script>

<script context="module">
    import appCssUrl from '../../app.css?inline';
    let style = '<style' + `>${appCssUrl}</style>`;
</script>

<div class="w-full px-4 sm:px-6 lg:px-8 py-2 flex justify-center">
    <div class="w-full max-w-4xl">
        {#if loading}
            <div class="w-full px-4 sm:px-6 lg:px-8 py-10 flex justify-center" data-testid="loading-state">
                <div class="w-full max-w-4xl">
                    <SkeletonLoaderBlog />
                </div>
            </div>
        {:else if error}
            <div class="py-16 text-center text-red-600 font-medium" data-testid="error-state">
                {error}
            </div>
        {:else if blog}
            <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="blog-detail">

                <!-- Header row with back button and section title -->
                <div class="flex items-center gap-3 mb-8" data-testid="header-row">
                    <Button
                        type="navigation"
                        class="w-[40px] h-[40px] flex items-center justify-center border border-[var(--vv-action-center-association-color,var(--vv-grey-power))] bg-white hover:bg-gray-50 p-1"
                        on:click={goBack}
                        aria-label="Back to blog list"
                        data-testid="back-button"
                    >
                        <CaretRightIcon
                            class="w-6 h-4"
                            style="transform: scaleX(-1);"
                            color="var(--vv-action-center-button-color)"
                        />
                    </Button>
                    <h1 class="text-2xl tracking-tight text-gray-800" data-testid="section-title">
                        {pageHeader}
                    </h1>
                </div>

                <!-- Blog title -->
                <h2 class="text-4xl text-gray-900 mb-8" data-testid="blog-title">
                    {blog.headline}
                </h2>

                <!-- Author and date -->
                <div class="flex items-center text-sm text-gray-600 mb-4 gap-2 flex-wrap" data-testid="blog-meta">
                    <span class="font-medium text-gray-700" data-testid="blog-author">{t.By_Label()} {blog.author}</span>
                    <div class="h-4 border-l-2 border-[var(--vv-action-center-association-color,var(--vv-grey-power))] mx-2"></div>
                    <span class="uppercase tracking-wide text-xs" data-testid="blog-date">
                        {blog.getDisplayedPublicationDate()}
                    </span>
                </div>

                <!-- Blog image -->
                {#if blog.imageUrl}
                    <img
                            src={blog.imageUrl}
                            alt={blog.headline}
                            class="w-full h-auto rounded-lg object-cover mb-6"
                            data-testid="blog-image"
                    />
                {:else}
                    <div class="w-full h-[2px] mb-6 bg-[var(--vv-action-center-association-color,var(--vv-grey-power))]" data-testid="blog-divider"></div>
                {/if}

                <!-- Blog content -->
                <div class="ck-content" data-testid="blog-content">
                    {@html style}
                    {@html blog.alert}
                </div>

                <!-- Share section -->
                <ShareSection
                  shareTitle={blog.headline}
                  shareUrl={currentUrl}
                  type="blog"
                  id={blog.id}
                />
            </div>
        {/if}
    </div>
</div>
